import { inject } from 'inversify';
import type { KyInstance, Options } from 'ky';
import { Injectable } from '../../decorators/injectable';
import { type IKyClientService, KyClientService } from '../ky-client/ky-client.service';

export interface IInspectionHttpService {
  setSessionToken(token: string | null): void;
  get<T>(url: string, options?: Options): Promise<T>;
  getBlob(url: string, options?: Options): Promise<Response>;
  post<T>(url: string, data?: unknown, options?: Options): Promise<T>;
  postBlob(url: string, data?: unknown, options?: Options): Promise<Response>;
}

@Injectable()
class InspectionHttpService implements IInspectionHttpService {
  private sessionToken: string | null = null;
  private readonly client: KyInstance;

  constructor(@inject(KyClientService) kyClientService: IKyClientService) {
    // Reuses the shared base config (prefix, no retry, Accept-Language, JSON
    // error-body extraction) but swaps the Clerk auth hook for the portal's
    // unauthenticated `x-inspection-token` header. Dossier zips need a longer
    // timeout than the default authenticated client.
    this.client = kyClientService.createClient({
      timeout: 30_000,
      authHooks: [
        ({ request }) => {
          if (this.sessionToken) {
            request.headers.set('x-inspection-token', this.sessionToken);
          }
        },
      ],
    });
  }

  setSessionToken(token: string | null): void {
    this.sessionToken = token;
  }

  async get<T>(url: string, options?: Options): Promise<T> {
    return this.client.get(url, options).json<T>();
  }

  async getBlob(url: string, options?: Options): Promise<Response> {
    return this.client.get(url, options);
  }

  async post<T>(url: string, data?: unknown, options?: Options): Promise<T> {
    return this.client.post(url, { json: data, ...options }).json<T>();
  }

  async postBlob(url: string, data?: unknown, options?: Options): Promise<Response> {
    return this.client.post(url, { json: data, ...options });
  }
}

export { InspectionHttpService };
